import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { monthlyGenerate, monthlyStatus, autopilotStatus, listData, updateData, strategyGet } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarPlus, Sparkle, CheckCircle, ArrowRight, Spinner, Warning, Lock, Robot, ListChecks,
  FacebookLogo, InstagramLogo, LinkedinLogo, EnvelopeSimple, ChatText, Article, Target } from "@phosphor-icons/react";
import { toast } from "sonner";
import GenerationOverlay from "@/components/GenerationOverlay";
import ItemDetailSheet from "@/components/ItemDetailSheet";

const OBJECTIVES = [
  { key: "brand_awareness", label: "Brand Awareness" },
  { key: "sales_growth", label: "Sales Growth" },
  { key: "product_launch", label: "Product Launch" },
  { key: "seasonal_promotion", label: "Seasonal Promotion" },
  { key: "customer_engagement", label: "Customer Engagement" },
  { key: "holiday_campaign", label: "Holiday Campaign" },
  { key: "custom", label: "Custom Theme" },
];
const CHANNELS = [
  { key: "facebook", label: "Facebook", icon: FacebookLogo },
  { key: "instagram", label: "Instagram", icon: InstagramLogo },
  { key: "linkedin", label: "LinkedIn", icon: LinkedinLogo },
  { key: "email", label: "Email", icon: EnvelopeSimple },
  { key: "sms", label: "SMS", icon: ChatText },
  { key: "blog", label: "Blog", icon: Article },
];
const CHANNEL_CHIP = {
  facebook: "bg-sky-500/20 text-sky-300", instagram: "bg-pink-500/20 text-pink-300",
  linkedin: "bg-[#0A66C2]/20 text-[#6db6f2]",
  email: "bg-emerald-500/20 text-emerald-300", sms: "bg-violet-500/20 text-violet-300",
  blog: "bg-amber-500/20 text-amber-300",
};
const STATUS_DOT = { pending: "bg-amber-400", approved: "bg-emerald-400", scheduled: "bg-sky-400", published: "bg-[#6B3DFF]", rejected: "bg-red-400" };

const monthOptions = () => {
  const out = [];
  const d = new Date();
  for (let k = 0; k < 12; k++) {
    const m = new Date(d.getFullYear(), d.getMonth() + k, 1);
    out.push({ value: `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`,
               label: m.toLocaleString("en-US", { month: "long", year: "numeric" }) });
  }
  return out;
};

export default function MonthlyPlannerPage() {
  const { activeId } = useClient();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const months = useMemo(monthOptions, []);
  const [month, setMonth] = useState(months[0].value);
  const [ppd, setPpd] = useState(1);
  const [channels, setChannels] = useState(["facebook", "instagram", "email", "sms", "blog"]);
  const [objective, setObjective] = useState("brand_awareness");
  const [theme, setTheme] = useState("");
  const [mode, setMode] = useState("approval");
  const [useStrategy, setUseStrategy] = useState(true);
  const [jobId, setJobId] = useState(null);
  const [overlay, setOverlay] = useState(false);
  const [startTs, setStartTs] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const { data: ap } = useQuery({
    queryKey: ["autopilot-status", activeId],
    queryFn: () => autopilotStatus(activeId), enabled: !!activeId,
  });
  const planAllows = ap?.plan_allows ?? false;
  const { data: strat } = useQuery({
    queryKey: ["strategy", activeId], queryFn: () => strategyGet(activeId), enabled: !!activeId,
  });
  const strategyActive = !!strat?.active;

  const { data: job } = useQuery({
    queryKey: ["monthly-job", jobId],
    queryFn: () => monthlyStatus(jobId),
    enabled: !!jobId, refetchInterval: (q) => (q.state.data?.status === "processing" ? 2500 : false),
  });
  const running = job?.status === "processing";
  React.useEffect(() => {
    if (job?.status === "done") {
      qc.invalidateQueries({ queryKey: ["data", "approvals", activeId] });
      toast.success(job.publish_mode === "autopilot"
        ? `${job.created} items generated & scheduled — Auto Pilot will publish them automatically`
        : `${job.created} items generated — review them in the Approval Queue`);
    }
    if (job?.status === "error") toast.error(job.error || "Generation failed");
  }, [job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: approvals = [], isLoading: approvalsLoading } = useQuery({
    queryKey: ["data", "approvals", activeId],
    queryFn: () => listData("approvals", activeId), enabled: !!activeId,
  });
  const monthItems = approvals.filter((a) => a.meta?.monthly && a.meta?.month === month && !a.archived);
  const detail = approvals.find((a) => a.id === detailId) || null;
  const pendingIds = monthItems.filter((a) => a.status === "pending").map((a) => a.id);
  const chCounts = useMemo(() => {
    const c = {};
    monthItems.forEach((a) => { const ch = a.meta?.channel || "facebook"; c[ch] = (c[ch] || 0) + 1; });
    return c;
  }, [monthItems]);

  const start = useMutation({
    mutationFn: () => monthlyGenerate({ client_id: activeId, month, posts_per_day: ppd, channels, objective, theme, publish_mode: mode, use_strategy: useStrategy }),
    onSuccess: (d) => { setJobId(d.job_id); setOverlay(true); setStartTs(Date.now()); if (d.reused) toast.info("A generation for this month is already running — reattached."); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not start generation"),
  });
  const approveAll = useMutation({
    mutationFn: async () => { for (const id of pendingIds) await updateData("approvals", id, { status: "approved" }); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["data", "approvals", activeId] }); toast.success("All items approved & auto-scheduled onto the Marketing Calendar"); },
  });

  const [y, m] = month.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const firstDow = new Date(y, m - 1, 1).getDay();
  const byDay = {};
  monthItems.forEach((a) => { const d = a.meta?.day || Number((a.scheduled_at || "").slice(8, 10)); (byDay[d] = byDay[d] || []).push(a); });

  const toggleChannel = (k) => setChannels((cur) => (cur.includes(k) ? (cur.length > 1 ? cur.filter((x) => x !== k) : cur) : [...cur, k]));
  const socialOn = channels.includes("facebook") || channels.includes("instagram") || channels.includes("linkedin");
  const estTotal = (socialOn ? daysInMonth * ppd : 0)
    + (channels.includes("email") ? Math.min(5, Math.ceil(daysInMonth / 7)) : 0)
    + (channels.includes("sms") ? 4 : 0) + (channels.includes("blog") ? 4 : 0);
  const pct = job?.total ? Math.min(100, Math.round(((job.created || 0) / job.total) * 100)) : 0;

  return (
    <PageContainer>
      {overlay && (job?.status === "processing" || job?.status === "done") && (
        <GenerationOverlay job={job} estTotal={estTotal}
          monthLabel={months.find((o) => o.value === month)?.label}
          elapsed={job?.finished_at && job?.created_at ? Math.max(1, Math.round((new Date(job.finished_at) - new Date(job.created_at)) / 1000)) : Math.round((Date.now() - (startTs || Date.now())) / 1000)}
          channelCounts={chCounts} autopilot={job?.publish_mode === "autopilot"}
          onViewCalendar={() => navigate("/scheduler")}
          onOpenQueue={() => navigate("/approvals")}
          onGenerateAnother={() => { setOverlay(false); setJobId(null); }}
          onDismiss={() => setOverlay(false)} />
      )}
      {detail && <ItemDetailSheet item={detail} onClose={() => setDetailId(null)} queryKey={["data", "approvals", activeId]} />}
      <PageHeader icon={CalendarPlus} title="AI Monthly Planner"
        subtitle="One click generates a complete, coordinated month across every channel — social, email, SMS and blog — with channels supporting each other around shared campaign moments." />

      <div className="card-matte rounded-2xl p-5 sm:p-6 mb-6" data-testid="monthly-generator-card">
        <div className="label-caps mb-2">Campaign objective</div>
        <div className="flex flex-wrap gap-2 mb-5" data-testid="monthly-objectives">
          {OBJECTIVES.map((o) => (
            <button key={o.key} data-testid={`monthly-objective-${o.key}`} onClick={() => setObjective(o.key)}
              className={`h-9 rounded-full px-4 text-xs font-semibold border transition-colors ${objective === o.key ? "bg-[#6B3DFF] border-[#6B3DFF] text-white" : "bg-white/5 border-white/10 text-[#9a9a9a] hover:text-white"}`}>
              {o.label}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <div className="label-caps mb-2">Month</div>
            <select data-testid="monthly-month-select" value={month} onChange={(e) => setMonth(e.target.value)}
              className="w-full h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-sm">
              {months.map((o) => <option key={o.value} value={o.value} className="bg-[#161622]">{o.label}</option>)}
            </select>
          </div>
          <div>
            <div className="label-caps mb-2">Social posts / day</div>
            <div className="flex gap-2">
              {[1, 2, 3].map((n) => (
                <button key={n} data-testid={`monthly-ppd-${n}`} onClick={() => setPpd(n)}
                  className={`h-10 flex-1 rounded-lg text-sm font-semibold border transition-colors ${ppd === n ? "bg-[#6B3DFF] border-[#6B3DFF] text-white" : "bg-white/5 border-white/10 text-[#9a9a9a] hover:text-white"}`}>{n}</button>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="label-caps mb-2">{objective === "custom" ? "Custom theme *" : "Extra focus (optional)"}</div>
            <Input data-testid="monthly-theme-input" value={theme} onChange={(e) => setTheme(e.target.value)}
              placeholder={objective === "custom" ? "Describe your campaign theme…" : "e.g. push the new service package"}
              className="h-10 bg-white/5 border-white/10" />
          </div>
        </div>

        <div className="mt-4">
          <div className="label-caps mb-2">Channels</div>
          <div className="flex flex-wrap gap-2" data-testid="monthly-channels">
            {CHANNELS.map((c) => (
              <button key={c.key} data-testid={`monthly-channel-${c.key}`} onClick={() => toggleChannel(c.key)}
                className={`h-10 rounded-lg px-4 flex items-center gap-2 text-sm font-semibold border transition-colors ${channels.includes(c.key) ? "bg-white/10 border-[#6B3DFF]/50 text-white" : "bg-white/5 border-white/10 text-[#9a9a9a]"}`}>
                <c.icon size={15} weight="fill" /> {c.label}
              </button>
            ))}
          </div>
        </div>

        {strategyActive && (
          <div className="mt-4 rounded-xl border border-[#6B3DFF]/30 bg-[#6B3DFF]/[0.06] p-3.5 flex flex-wrap items-center gap-3" data-testid="monthly-strategy-banner">
            <Target size={16} className="brand-text shrink-0" weight="fill" />
            <div className="flex-1 min-w-[220px]">
              <div className="text-sm font-semibold">Content Strategy Mix {useStrategy ? "active" : "ignored"}</div>
              <div className="text-[11px] text-[#9a9a9a]">Facebook & Instagram slots follow your per-platform weekly mix (lifestyle, product, reels, carousels…) instead of "{ppd}/day". <button onClick={() => navigate("/strategy")} className="brand-text hover:text-[#A56CFF]" data-testid="monthly-goto-strategy">Edit strategy →</button></div>
            </div>
            <button data-testid="monthly-use-strategy-toggle" onClick={() => setUseStrategy((v) => !v)} role="switch" aria-checked={useStrategy}
              className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${useStrategy ? "bg-[#6B3DFF]" : "bg-white/15"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${useStrategy ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>
        )}

        <div className="mt-4">
          <div className="label-caps mb-2">Publishing mode</div>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="monthly-publish-mode">
            <button data-testid="monthly-mode-approval" onClick={() => setMode("approval")}
              className={`text-left rounded-xl border p-4 transition-all ${mode === "approval" ? "border-[#6B3DFF]/50 bg-[#6B3DFF]/[0.07]" : "border-white/8 bg-white/[0.03] hover:border-white/20"}`}>
              <div className="flex items-center gap-2 font-head font-semibold text-sm">
                <ListChecks size={16} className="brand-text" weight="duotone" /> Approval Mode
                <span className="text-[9px] font-bold rounded-full bg-white/10 px-2 py-0.5 text-[#9a9a9a]">DEFAULT</span>
              </div>
              <p className="mt-1.5 text-xs text-[#9a9a9a] leading-relaxed">Everything waits in your Approval Queue. Approve once and items auto-schedule onto the Marketing Calendar — nothing publishes without your sign-off.</p>
            </button>
            <button data-testid="monthly-mode-autopilot" onClick={() => planAllows && setMode("autopilot")}
              disabled={!planAllows}
              className={`relative text-left rounded-xl border p-4 transition-all ${mode === "autopilot" ? "border-emerald-500/50 bg-emerald-500/[0.06]" : "border-white/8 bg-white/[0.03]"} ${planAllows ? "hover:border-white/20 cursor-pointer" : "opacity-60 cursor-not-allowed"}`}>
              <div className="flex items-center gap-2 font-head font-semibold text-sm">
                <Robot size={16} className={planAllows ? "text-emerald-400" : "text-[#7a7a7a]"} weight="duotone" /> Auto Pilot
                <span className="text-[9px] font-bold rounded-full bg-[#6B3DFF]/15 brand-text px-2 py-0.5">PREMIUM</span>
                {!planAllows && <Lock size={12} className="text-[#7a7a7a] ml-auto" weight="fill" />}
              </div>
              <p className="mt-1.5 text-xs text-[#9a9a9a] leading-relaxed">
                {planAllows
                  ? "Vozelto publishes every item automatically at its scheduled date & time. Pause, edit or cancel anything before it goes live. Failed publishes retry and notify you."
                  : "Available on Growth, Agency and Enterprise plans. Upgrade to let Vozelto publish your entire month automatically."}
              </p>
            </button>
          </div>
          {mode === "autopilot" && (
            <div className="mt-2 flex items-center gap-2 text-xs text-amber-300" data-testid="monthly-autopilot-warning">
              <Warning size={13} weight="fill" /> Auto Pilot skips manual approval — every generated item will publish automatically on schedule. You can pause Auto Pilot or edit items anytime.
            </div>
          )}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Button data-testid="monthly-generate-btn" disabled={running || start.isPending || !activeId || (objective === "custom" && !theme.trim())} onClick={() => start.mutate()}
            className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-11 px-6">
            {running ? <><Spinner size={16} className="mr-2 animate-spin" /> Generating… {job?.created || 0}/{job?.total || estTotal}</>
              : <><Sparkle size={16} className="mr-2" weight="fill" /> Generate Monthly Campaign (~{estTotal} items)</>}
          </Button>
          {pendingIds.length > 0 && !running && (
            <Button data-testid="monthly-approve-all-btn" variant="ghost" disabled={approveAll.isPending} onClick={() => approveAll.mutate()}
              className="rounded-full border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 h-11 px-5">
              <CheckCircle size={16} className="mr-2" weight="fill" /> {approveAll.isPending ? "Approving…" : `Approve all ${pendingIds.length} & schedule`}
            </Button>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="ghost" data-testid="monthly-goto-approvals" onClick={() => navigate("/approvals")} className="rounded-full border border-white/10 text-white hover:bg-white/5 h-11 px-4 text-sm">Approval Queue <ArrowRight size={14} className="ml-1.5" /></Button>
            <Button variant="ghost" data-testid="monthly-goto-calendar" onClick={() => navigate("/scheduler")} className="rounded-full border border-white/10 text-white hover:bg-white/5 h-11 px-4 text-sm">Marketing Calendar <ArrowRight size={14} className="ml-1.5" /></Button>
          </div>
        </div>
        {running && (
          <div className="mt-4 h-1.5 rounded-full bg-white/8 overflow-hidden">
            <div className="h-full bg-[#6B3DFF] transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>

      <div className="card-matte rounded-2xl p-4 sm:p-5 fade-up" data-testid="monthly-calendar-preview">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div>
            <h3 className="font-head font-bold text-lg" data-testid="monthly-campaign-header">{months.find((o) => o.value === month)?.label} Marketing Campaign</h3>
            <div className="text-xs text-[#9a9a9a]">
              {monthItems.length > 0
                ? <>{monthItems.length} AI-generated marketing assets · <span className="brand-text">Generated by your Vozelto AI Team</span></>
                : "No assets yet — your AI team is ready when you are"}
            </div>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-[#9a9a9a]" data-testid="monthly-channel-counts">
            {CHANNELS.map((c) => chCounts[c.key] ? (
              <span key={c.key} className="flex items-center gap-1"><c.icon size={11} weight="fill" /> {chCounts[c.key]} {c.label.toLowerCase()}</span>
            ) : null)}
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-[#7a7a7a] mb-1">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDow }).map((_, k) => <div key={`e${k}`} />)}
          {Array.from({ length: daysInMonth }).map((_, k) => {
            const d = k + 1;
            const items = byDay[d] || [];
            return (
              <div key={d} data-testid={`monthly-day-${d}`} className="min-h-[72px] rounded-lg bg-white/[0.03] border border-white/8 p-1.5">
                <div className="text-[10px] text-[#7a7a7a] mb-1">{d}</div>
                {items.slice(0, 4).map((a) => (
                  <div key={a.id} onClick={() => setDetailId(a.id)} title={`${a.title}\nCreated by ${a.created_by || "Vozelto"}\n\n${(a.body || "").slice(0, 140)}`}
                    className={`mb-1 rounded px-1 py-0.5 text-[9px] leading-tight truncate flex items-center gap-1 cursor-pointer transition-transform hover:scale-[1.04] ${CHANNEL_CHIP[a.meta?.channel] || "bg-white/10 text-white"}`}
                    data-testid={`monthly-chip-${a.id}`}>
                    <span className={`h-1 w-1 rounded-full shrink-0 ${STATUS_DOT[a.status] || "bg-white/40"}`} />
                    {(a.meta?.channel || "post")}{a.meta?.content_type ? ` · ${a.meta.content_type.replace(/_/g, " ")}` : ""}
                  </div>
                ))}
                {items.length > 4 && <div className="text-[8px] text-[#7a7a7a]">+{items.length - 4} more</div>}
              </div>
            );
          })}
        </div>
        {approvalsLoading && (
          <div className="grid grid-cols-7 gap-1 mt-1" data-testid="monthly-skeleton">
            {Array.from({ length: 14 }).map((_, k) => <div key={k} className="h-[72px] rounded-lg bg-white/[0.05] animate-pulse" />)}
          </div>
        )}
        {monthItems.length === 0 && !running && !approvalsLoading && (
          <div className="text-center py-10" data-testid="monthly-empty-state">
            <div className="inline-flex h-14 w-14 rounded-2xl bg-[#6B3DFF]/10 items-center justify-center mb-3">
              <Sparkle size={26} className="brand-text" weight="duotone" />
            </div>
            <div className="font-head font-semibold">Your month is a blank canvas</div>
            <div className="text-sm text-[#7a7a7a] mt-1 max-w-sm mx-auto">Pick an objective and channels above, then hit Generate — your AI team will fill this calendar in about a minute.</div>
          </div>
        )}
      </div>
    </PageContainer>
  );
}
